import cn from 'classnames';
import { useRouter } from 'next/router';
import { useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
// API
import { GET_MEDIA_POSTS } from 'api/mediaPost/queries';
import { GET_ARTICLES } from 'api/articles/queries';
// Types
import {
  ArticleStatus,
  MemorabiliaOrderBy,
  MemorabiliaStatus,
  StreamStatus,
  StoreStatus,
  UserRole,
} from 'api/graphql-global-types';
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import {
  GetMediaPosts,
  GetMediaPostsVariables,
} from 'api/mediaPost/types/GetMediaPosts';
import {
  GetArticleForStore,
  GetArticleForStoreVariables,
} from 'api/articles/types/GetArticleForStore';
// Helpers
import { getCheapestActiveAma } from 'helpers/ama';
import { computeShopPath, getEnvLink } from 'helpers/routes';
import { getAthleteProfileSeo } from 'helpers/athleteProfileSeo';
import { isUserCustomer } from 'helpers/user';
// Hooks
import {
  useAthleteRelatedStreams,
  useGetAthleteMemorabilia,
  useGetCurrUser,
  useStoreFeaturedParticipation,
  useGetFeaturedStreams,
  useGetAthleteStreams,
} from 'hooks';
import { useTrackProfileView } from 'hooks/analytics';
// Layouts
import { getProfileAvatarPreload } from 'helpers/profileAvatar';
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// Components
import Ama from '../Ama/Ama';
import Description from '../Description/Description';
import Memorabilia from '../Memorabilia/Memorabilia';
import RelatedMediaFeed from 'components/MediaFeed/RelatedMediaFeed/RelatedMediaFeed';
import Articles from '../Articles/Articles';
import ProfileFeaturedStreams from '../ProfileFeaturedStreams/ProfileFeaturedStreams';
import Streams from '../Streams/Streams';
import DownloadAppSection from 'components/HomePage/components/V3/GetAppCta/GetApp/DownloadAppSection';
import BecomeACreatorCTA from '../BecomeACreatorCTA/BecomeACreatorCTA';
import ShopNowSection from '../ShopNowSection/ShopNowSection';
import LiveStream from '../Streams/LiveStream/LiveStream';
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
// Styles
import styles from './AthleteContainer.module.scss';

type AthleteContainerProps = {
  store?: GetStoreBySlug_store;
  isPending?: boolean;
  padding?: 'default' | 'none';
  loading?: boolean;
};

const AthleteContainer = ({
  store,
  padding = 'none',
  loading: loadingStore = false,
}: AthleteContainerProps) => {
  const router = useRouter();
  const athleteSlug = router.query.athleteSlug;
  const hasTrackedProfileView = useRef(false);

  // Get current user to check if they're a fan
  const { data: userData } = useGetCurrUser();
  const isFan = userData && isUserCustomer(userData);

  const { trackProfileView, reset: resetProfileTracking } = useTrackProfileView(
    {
      profileId: store?.id || null,
    }
  );

  useEffect(() => {
    if (store?.id && !loadingStore && !hasTrackedProfileView.current) {
      hasTrackedProfileView.current = true;
      trackProfileView();
    }
  }, [store?.id, loadingStore, trackProfileView]);

  useEffect(() => {
    return () => {
      hasTrackedProfileView.current = false;
      resetProfileTracking();
    };
  }, [athleteSlug, resetProfileTracking]);

  const { streams, total: totalStreams } = useAthleteRelatedStreams({
    storeId: store?.id || '',
    totalStreamsToShow: 1,
  });

  // Check store's participation in featured streams (as participant or host)
  const {
    isStoreHostInFeaturedStreams,
    isStoreParticipantInFeaturedStreams,
  } = useStoreFeaturedParticipation({ store });
  const isStoreInFeaturedStreams =
    isStoreHostInFeaturedStreams || isStoreParticipantInFeaturedStreams;

  // NOTE: These prefetch hooks used to feed a unified-loading gate that hid
  // the whole content area until every section had data. That tanked LCP
  // (~3.4s on mobile dev) because the LCP element couldn't paint until
  // every dependent query — including ones below the fold — resolved. Each
  // section now renders progressively with its own loading state.
  useGetFeaturedStreams();
  useGetAthleteStreams({
    storeId: !isStoreInFeaturedStreams && store?.id ? store.id : undefined,
    liveAndUpcomingStreamsInput: { limit: 1, offset: 0 },
    pastStreamsInput: { limit: 1, offset: 0 },
  });

  useQuery<GetMediaPosts, GetMediaPostsVariables>(GET_MEDIA_POSTS, {
    variables: {
      input: {
        storeId: store?.id || '',
        storeStatuses: [StoreStatus.Active],
        limit: 8,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !store?.id || !store?.hasMediaPost,
  });

  useQuery<GetArticleForStore, GetArticleForStoreVariables>(GET_ARTICLES, {
    variables: {
      input: {
        storeId: store?.id || '',
        statuses: [ArticleStatus.Active],
        limit: 9,
        offset: 0,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !store?.id || !store?.hasArticle,
  });

  // Determine if streams carousel should be shown (UI logic in component)
  const hasMultipleStreams = totalStreams > 1;
  const hasSingleStream = totalStreams === 1;
  const isParticipantOnly =
    isStoreParticipantInFeaturedStreams && !isStoreHostInFeaturedStreams;

  // Show carousel when:
  // 1. Owner has 2+ streams → Always show all streams
  // 2. Owner has 1 stream AND is only participant in featured → Show their stream
  const shouldShowStreamsCarousel =
    store?.hasStreams &&
    (hasMultipleStreams || (hasSingleStream && isParticipantOnly));

  const { data } = useGetAthleteMemorabilia({
    storeId: store?.id || '',
    variables: {
      statuses: [MemorabiliaStatus.Active],
      limit: 1,
      orderBy: MemorabiliaOrderBy.price,
    },
    skip:
      !store ||
      (!store.hasMemorabilia && !store.hasPureProducts) ||
      store.hasActiveStreams,
  });

  // Filter to only get live streams (Active) that belong to this store
  const liveStream = useMemo(() => {
    if (!store?.id || !streams?.length) {
      return undefined;
    }

    return streams.find((stream) => {
      const isLiveStatus = stream.streamStatus === StreamStatus.Active;
      const belongsToStore = stream.store?.id === store.id;

      return isLiveStatus && belongsToStore;
    });
  }, [streams, store?.id]);

  const displayLiveStream = !!liveStream;

  if (!store) {
    return null;
  }

  const memorabilia = data?.getMemorabilia?.entities?.[0];
  const avatarUrl = store.storeDetails?.avatarURL || '';
  const {
    metaTitle,
    metaDescription,
    structuredData: athleteSeoStructuredData,
  } = getAthleteProfileSeo(
    store,
    typeof athleteSlug === 'string' ? athleteSlug : store.slug || ''
  );
  const cheapestActiveAma = getCheapestActiveAma(store.amas || []);
  const shouldShowAma =
    cheapestActiveAma && store.role !== UserRole.Organization;

  // Determine rendering conditions for AMA and CTA
  const hasAmaToShow = shouldShowAma && !store.hasActiveStreams;
  const shouldShowAmaWithCta = hasAmaToShow && isFan;
  const shouldShowAmaAlone = hasAmaToShow && !isFan;
  const shouldShowCtaAlone = !hasAmaToShow && isFan;

  const hasProducts =
    store.hasMerch ||
    store.hasMemorabilia ||
    store.hasPureProducts ||
    store.hasExperiences;

  const seeMediaLink = `${athleteSlug}/media`;
  const seeShopLink = computeShopPath(store);

  const isNotEmpty =
    store.hasMemorabilia ||
    hasProducts ||
    store.hasAma ||
    store.hasExperiences ||
    store.hasStreams ||
    store.hasMediaPost ||
    store.hasArticle ||
    isStoreInFeaturedStreams ||
    displayLiveStream;

  const canonicalLink = `${getEnvLink()}/${athleteSlug}`;
  const isAthletePending = store?.status === StoreStatus.Pending;

  return (
    <>
      <Head
        title={metaTitle}
        description={metaDescription}
        image={avatarUrl}
        imagePreload={getProfileAvatarPreload(avatarUrl)}
        imageAlt={[
          store.storeDetails?.storeName || 'Athlete',
          store.storeDetails?.storeTitle,
          store.sports?.[0]?.name,
        ]
          .filter(Boolean)
          .join(' — ')}
        canonicalLink={canonicalLink}
        structuredData={athleteSeoStructuredData}
      />
      <AppBannerAndModal
        when={!!store && !loadingStore}
        variant="profile"
        username={typeof athleteSlug === 'string' ? athleteSlug : undefined}
        profileImageUrl={avatarUrl || undefined}
        redirectToStoreOnly={displayLiveStream}
      />
      <Header />

      <Main withoutSidesPadding>
        <Description
          store={store}
          isAthletePending={isAthletePending}
          hasActiveLiveStream={displayLiveStream}
        />
        {isNotEmpty && (
          <div
            className={cn(styles.root, {
              [styles.withPadding]: padding === 'default',
            })}
          >
            {/*
              Content renders progressively. Each section component handles
              its own loading state (skeletons/spinners). The previous
              unified gate hid everything behind one overlay loader, which
              pushed LCP back to whenever the slowest sub-query resolved.
            */}
            <div className={styles.contentWrapper}>
              {displayLiveStream && <LiveStream stream={liveStream} />}

              {(store.hasStreams || isStoreInFeaturedStreams) && (
                <ProfileFeaturedStreams
                  store={store}
                  isProfileOwnerInFeaturedStreams={isStoreInFeaturedStreams}
                />
              )}

              {shouldShowStreamsCarousel && (
                <Streams
                  storeId={store.id}
                  storeSlug={store.slug}
                  storeRole={store.role}
                  hasActiveStream={store.hasActiveStreams}
                  arrowsBelowOnMobile
                  className={styles.adjustedPosition}
                  displayLiveStream={false}
                />
              )}

              {store.hasMediaPost && (
                <RelatedMediaFeed
                  title="Media Feed"
                  store={store}
                  className={styles.adjustedPosition}
                  link={seeMediaLink}
                />
              )}

              {(store.hasMemorabilia || store.hasPureProducts) &&
                !store.hasActiveStreams &&
                memorabilia && (
                  <Memorabilia
                    store={store}
                    memorabilia={memorabilia}
                    isPage={false}
                  />
                )}

              {shouldShowAmaWithCta && (
                <div className={cn(styles.amaCtaGrid, styles.hasAma)}>
                  <div className={styles.amaGridItem}>
                    <Ama
                      ama={cheapestActiveAma}
                      store={store}
                      className={styles.amaInWrapper}
                    />
                  </div>
                  <div className={styles.ctaGridItem}>
                    <BecomeACreatorCTA
                      variant="inline"
                      className={styles.ctaInWrapper}
                    />
                  </div>
                </div>
              )}

              {shouldShowAmaAlone && (
                <Ama ama={cheapestActiveAma} store={store} />
              )}

              {shouldShowCtaAlone && (
                <BecomeACreatorCTA
                  variant="standalone"
                  className={styles.adjustedPosition}
                />
              )}

              {hasProducts && (
                <ShopNowSection
                  seeAllLink={seeShopLink}
                  className={styles.adjustedPosition}
                  storeId={store.id}
                />
              )}

              {store.hasArticle && (
                <Articles
                  storeId={store.id}
                  className={styles.adjustedPosition}
                />
              )}
            </div>
          </div>
        )}
      </Main>
      <DownloadAppSection />
      <Footer />
    </>
  );
};

export default AthleteContainer;
